import React, { useEffect, useState, CSSProperties } from "react";
import { PlusCircle, X, Trash2, ChevronDown, ChevronUp, Upload, Camera, Video, AlertCircle, Package } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState, AppDispatch } from "../../redux/store";
import {
  upsertClassified,
  upsertClassifiedMedia,
  deleteClassified,
  deleteClassifiedPhoto,
  listenToHostClassified,
  ClassifiedListing,
  setListings,
} from "../../redux/classifiedsSlice";
import { listenToUserCommercePartners } from "../../redux/commercePartnersSlice";

import {
  electronicsAttributes,
  electronicsColorAttributes,
  electronicsSizeAttributes,
  ElectronicsAttributeGroup,
} from "../ATTRIBUTES/ElectronicsAttributes";
import {
  electronicsCategories,
  electronicsSubcategories,
  electronicsProducts,
} from "../CATEGORIES/ElectronicsCategories";

/* ════════════════════════════════════════════════════════════════
   DESIGN TOKENS — matches Glamour exactly
════════════════════════════════════════════════════════════════ */
const T = {
  bg:         "#FFFFFF",
  bgSoft:     "#FAFAFA",
  bgMuted:    "#F5F5F3",
  black:      "#0A0A0A",
  blackMid:   "#1C1C1C",
  blackSoft:  "#2C2C2C",
  border:     "#E8E8E6",
  borderDark: "#C8C8C4",
  muted:      "#8A8A86",
  mutedSoft:  "#B8B8B4",
  gold:       "#C9A96E",
  goldLight:  "#F0E2C8",
};

/* ─── Category groups ─── */
const CATEGORY_GROUPS = [
  {
    name: "Electronics",
    categories: electronicsCategories,
    subs: electronicsSubcategories,
    products: electronicsProducts,
  },
];

const MAX_IMAGES = 5;

/* ════════════════════════════════════════════════════════════════
   TYPES
════════════════════════════════════════════════════════════════ */
interface NewCommerce {
  id:           string;
  listingId?:   string;
  groupIndex:   number | null;
  categoryId:   number | null;
  subcategoryId: number | null;
  productId:    number | null;
  title:        string;
  description:  string;
  stock:        number;
  rate:         number;
  available:    boolean;
  images:       File[];
  photos?:      string[];
  video?:       string | null;
  videoFile?:   File | null;
  attributes?:  Record<number, number | number[]>;
  variants?:    Record<number, {
    imageUrl?:   string;
    imageFile?:  File;
    previewUrl?: string;
    sizeIds:     number[];
    stock:       number;
  }>;
  dimensions?: {
    unit:        "cm" | "inch";
    weightUnit:  "g" | "ml";
    height?:     number;
    width?:      number;
    length?:     number;
    netWeight?:  number;
  };
  edited?:      boolean;
  isPublished:  boolean;
}

/* ════════════════════════════════════════════════════════════════
   HELPERS
════════════════════════════════════════════════════════════════ */
const findGroupIndexByCategoryId = (categoryId: number | null) => {
  if (categoryId == null) return null;
  return CATEGORY_GROUPS.findIndex(g => g.categories.some(c => c.id === categoryId));
};

const fmtPrice = (n: number) =>
  `€${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ════════════════════════════════════════════════════════════════
   SHARED UI COMPONENTS — Glamour design system
════════════════════════════════════════════════════════════════ */

/* ── Luxury Toggle ── */
const LuxToggle: React.FC<{ on: boolean; onChange: (v: boolean) => void }> = ({ on, onChange }) => (
  <button
    onClick={() => onChange(!on)}
    style={{
      width: 44, height: 24, borderRadius: 12, border: "none",
      background: on ? T.black : T.border, cursor: "pointer",
      position: "relative", transition: "background .22s", flexShrink: 0,
    }}
  >
    <div style={{
      position: "absolute", top: 3,
      left: on ? 23 : 3, width: 18, height: 18,
      borderRadius: 9, background: T.bg,
      transition: "left .22s",
      boxShadow: "0 1px 4px rgba(0,0,0,.25)",
    }} />
  </button>
);

/* ── Snackbar ── */
const Snack: React.FC<{ visible: boolean; msg: string; type: "ok" | "err" | "warn" }> = ({ visible, msg, type }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        style={{
          position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
          background: type === "ok" ? T.black : type === "err" ? "#8B0000" : "#7A5C00",
          color: T.bg, fontFamily: "'Montserrat', sans-serif",
          fontSize: 12, fontWeight: 600, letterSpacing: "0.1em",
          padding: "13px 28px", zIndex: 9999,
          boxShadow: "0 8px 32px rgba(0,0,0,.24)", whiteSpace: "nowrap",
        }}
      >
        {msg.toUpperCase()}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ── Section label ── */
const SLabel: React.FC<{ children: React.ReactNode; sub?: string }> = ({ children, sub }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.22em", color: T.muted, fontFamily: "'Montserrat', sans-serif" }}>
      {String(children).toUpperCase()}
    </div>
    {sub && <div style={{ fontSize: 11, color: T.mutedSoft, fontFamily: "'Montserrat', sans-serif", marginTop: 2 }}>{sub}</div>}
  </div>
);

/* ── Field wrapper ── */
const Field: React.FC<{ label: string; children: React.ReactNode; required?: boolean }> = ({ label, children, required }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 7 }}>
      {label.toUpperCase()}{required && <span style={{ color: T.gold, marginLeft: 3 }}>✦</span>}
    </div>
    {children}
  </div>
);

/* ── Luxury input ── */
const LInput: React.FC<{
  value: string | number;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  rows?: number;
  prefix?: string;
  disabled?: boolean;
}> = ({ value, onChange, placeholder, type = "text", rows, prefix, disabled }) => (
  <div style={{ display: "flex", border: `1px solid ${T.border}`, background: disabled ? T.bgMuted : T.bgSoft, opacity: disabled ? 0.5 : 1 }}>
    {prefix && (
      <div style={{
        padding: "0 14px", display: "flex", alignItems: "center",
        background: T.bgMuted, borderRight: `1px solid ${T.border}`,
        fontSize: 13, color: T.muted, fontFamily: "'Cormorant Garamond', serif",
      }}>
        {prefix}
      </div>
    )}
    {rows ? (
      <textarea
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} rows={rows} disabled={disabled}
        style={{
          flex: 1, border: "none", outline: "none", padding: "11px 14px",
          fontSize: 13, fontFamily: "'Montserrat', sans-serif", color: T.black,
          background: "transparent", resize: "vertical", lineHeight: 1.6,
        }}
      />
    ) : (
      <input
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} type={type} disabled={disabled}
        style={{
          flex: 1, border: "none", outline: "none", padding: "11px 14px",
          fontSize: 13, fontFamily: "'Montserrat', sans-serif", color: T.black,
          background: "transparent",
        }}
      />
    )}
  </div>
);

/* ── Select ── */
const LSelect: React.FC<{
  value: number | string;
  onChange: (v: number) => void;
  options: { id: number; name: string }[];
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, options, placeholder = "Select…", disabled }) => (
  <div style={{ position: "relative" }}>
    <select
      value={value ?? ""}
      onChange={e => onChange(+e.target.value)}
      disabled={disabled}
      style={{
        width: "100%", border: `1px solid ${T.border}`,
        background: disabled ? T.bgMuted : T.bgSoft,
        color: value ? T.black : T.muted,
        padding: "11px 40px 11px 14px", fontSize: 13,
        fontFamily: "'Montserrat', sans-serif", outline: "none",
        appearance: "none", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <option value="" disabled>{placeholder}</option>
      {options.map(o => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
    <ChevronDown size={14} color={T.muted} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
  </div>
);

/* ── Multi-select for attributes ── */
const LMultiSelect: React.FC<{
  value: number[];
  onChange: (v: number[]) => void;
  options: { id: number; name: string }[];
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, options, placeholder = "Select…", disabled }) => (
  <div style={{ position: "relative" }}>
    <select
      multiple
      value={value.map(String)}
      onChange={e => {
        const selected = Array.from(e.target.selectedOptions).map(o => +o.value);
        onChange(selected);
      }}
      disabled={disabled}
      style={{
        width: "100%", border: `1px solid ${T.border}`,
        background: disabled ? T.bgMuted : T.bgSoft,
        color: T.black, padding: "4px 0", fontSize: 13,
        fontFamily: "'Montserrat', sans-serif", outline: "none",
        opacity: disabled ? 0.5 : 1, minHeight: 80,
      }}
    >
      {options.map(o => (
        <option key={o.id} value={o.id} style={{ padding: "6px 14px" }}>{o.name}</option>
      ))}
    </select>
    <div style={{ fontSize: 10, color: T.mutedSoft, fontFamily: "'Montserrat', sans-serif", marginTop: 4 }}>
      Hold Ctrl / Cmd to select multiple
    </div>
  </div>
);

/* ── Colour swatch variant card ── */
const ElectronicsVariantCard: React.FC<{
  colorId:  number;
  variant:  NonNullable<NewCommerce["variants"]>[number] | undefined;
  itemId:   string;
  stock:    number;
  onUpdate: (colorId: number, patch: Partial<NonNullable<NewCommerce["variants"]>[number]>) => void;
  onRemove: (colorId: number) => void;
}> = ({ colorId, variant, itemId, stock, onUpdate, onRemove }) => {
  const color = electronicsColorAttributes.values.find(v => v.id === colorId);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      style={{
        border: `1px solid ${T.border}`, background: T.bgSoft,
        padding: 16, position: "relative", marginBottom: 12,
      }}
    >
      <button onClick={() => onRemove(colorId)} style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", cursor: "pointer", color: T.muted }}>
        <X size={14} />
      </button>

      {/* Colour header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        {color?.hexCodes?.[0] && (
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: color.hexCodes[0], border: `1px solid ${T.border}` }} />
        )}
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", fontFamily: "'Montserrat', sans-serif", color: T.black }}>
          {color?.name?.toUpperCase()}
        </span>
      </div>

      <div style={{ display: "flex", gap: 14 }}>
        {/* Image upload */}
        <label style={{
          width: 90, height: 110, background: T.bgMuted,
          border: `1px dashed ${T.borderDark}`,
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", cursor: "pointer", overflow: "hidden",
          flexShrink: 0, gap: 6,
        }}>
          {variant?.previewUrl || variant?.imageUrl ? (
            <img src={variant.previewUrl ?? variant.imageUrl} alt={color?.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <>
              <Camera size={18} color={T.muted} />
              <span style={{ fontSize: 9, color: T.muted, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.1em" }}>UPLOAD</span>
            </>
          )}
          <input type="file" hidden accept="image/*" onChange={e => {
            const f = e.target.files?.[0];
            if (!f) return;
            onUpdate(colorId, { imageFile: f, previewUrl: URL.createObjectURL(f) });
          }} />
        </label>

        {/* Stock */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, letterSpacing: "0.18em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 6 }}>STOCK</div>
          <input
            type="number"
            value={variant?.stock ?? stock ?? 0}
            onChange={e => onUpdate(colorId, { stock: +e.target.value })}
            style={{
              border: `1px solid ${T.border}`, background: T.bg,
              padding: "8px 12px", fontSize: 13,
              fontFamily: "'Montserrat', sans-serif", width: "100%",
              outline: "none", color: T.black,
            }}
          />
        </div>
      </div>
    </motion.div>
  );
};

/* ════════════════════════════════════════════════════════════════
   ITEM CARD
════════════════════════════════════════════════════════════════ */
interface ItemCardProps {
  item:       NewCommerce;
  expanded:   boolean;
  onToggle:   () => void;
  onUpdate:   (id: string, key: keyof NewCommerce, value: any) => void;
  onSave:     (item: NewCommerce) => void;
  onDelete:   (listingId: string) => void;
  onDelPhoto: (listingId: string, url: string) => void;
  saving:     boolean;
}

const ItemCard: React.FC<ItemCardProps> = ({
  item, expanded, onToggle, onUpdate, onSave, onDelete, onDelPhoto, saving,
}) => {
  const up = (key: keyof NewCommerce, value: any) => onUpdate(item.id, key, value);
  const thumb = item.photos?.[0] ?? (item.images?.[0] ? URL.createObjectURL(item.images[0]) : null);

  const group = item.groupIndex !== null ? CATEGORY_GROUPS[item.groupIndex] : null;
  const subs  = group ? group.subs.filter((s: any) => s.parentId === item.categoryId) : [];
  const prods = group ? group.products.filter((p: any) => p.parentId === item.subcategoryId) : [];

  const isElectronics = item.groupIndex !== null && CATEGORY_GROUPS[item.groupIndex]?.name === "Electronics";
  const colorValue    = item.attributes?.[electronicsColorAttributes.id];
  const selectedColors: number[] = Array.isArray(colorValue) ? colorValue : [];
  const hasVariants   = isElectronics && selectedColors.length > 0;

  const updateVariant = (colorId: number, patch: Partial<NonNullable<NewCommerce["variants"]>[number]>) => {
    up("variants", {
      ...item.variants,
      [colorId]: { sizeIds: [], stock: 0, ...item.variants?.[colorId], ...patch },
    });
  };

  const removeVariantAndColor = (colorId: number) => {
    const newColors = selectedColors.filter(c => c !== colorId);
    up("attributes", { ...item.attributes, [electronicsColorAttributes.id]: newColors });
    const newVariants = { ...item.variants };
    delete newVariants[colorId];
    up("variants", newVariants);
  };

  const handleColorToggle = (colorId: number, checked: boolean) => {
    const newColors = checked
      ? [...selectedColors, colorId]
      : selectedColors.filter(c => c !== colorId);
    up("attributes", { ...item.attributes, [electronicsColorAttributes.id]: newColors });
    if (checked && !item.variants?.[colorId]) {
      up("variants", { ...item.variants, [colorId]: { sizeIds: [], stock: item.stock ?? 0 } });
    }
    if (!checked) {
      const nv = { ...item.variants };
      delete nv[colorId];
      up("variants", nv);
    }
  };

  return (
    <motion.div
      layout
      style={{
        background: T.bg, border: `1px solid ${T.border}`,
        marginBottom: 12, overflow: "hidden",
        boxShadow: expanded ? "0 8px 40px rgba(0,0,0,.07)" : "0 2px 8px rgba(0,0,0,.03)",
        transition: "box-shadow .3s",
      }}
    >
      {/* ── HEADER ── */}
      <div
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "14px 18px", cursor: "pointer",
          background: expanded ? T.bgSoft : T.bg,
          borderBottom: expanded ? `1px solid ${T.border}` : "none",
          transition: "background .2s",
        }}
      >
        {/* Thumb */}
        <div style={{ width: 52, height: 64, background: T.bgMuted, overflow: "hidden", flexShrink: 0 }}>
          {thumb
            ? <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Package size={20} color={T.mutedSoft} />
              </div>
          }
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Badges */}
          <div style={{ display: "flex", gap: 6, marginBottom: 5, flexWrap: "wrap" }}>
            {group && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: T.gold, background: T.goldLight, padding: "2px 8px", fontFamily: "'Montserrat', sans-serif" }}>
                {group.name.toUpperCase()}
              </span>
            )}
            {item.isPublished && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: T.bg, background: T.black, padding: "2px 8px", fontFamily: "'Montserrat', sans-serif" }}>
                LIVE
              </span>
            )}
            {!item.isPublished && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: T.muted, background: T.bgMuted, padding: "2px 8px", fontFamily: "'Montserrat', sans-serif", border: `1px solid ${T.border}` }}>
                DRAFT
              </span>
            )}
            {item.edited && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: "#7A5C00", background: "#FDF7E3", padding: "2px 8px", fontFamily: "'Montserrat', sans-serif" }}>
                UNSAVED
              </span>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: T.black, fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.title || "Untitled Item"}
          </div>
          <div style={{ fontSize: 12, color: T.muted, fontFamily: "'Montserrat', sans-serif", marginTop: 2 }}>
            {item.rate ? fmtPrice(item.rate) : "Set price"}
          </div>
        </div>

        <div style={{ color: T.muted, flexShrink: 0 }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {/* ── EXPANDED BODY ── */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ padding: "24px 22px" }}
          >

            {/* ══ SECTION 1: CLASSIFICATION ══ */}
            <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${T.border}` }}>
              <SLabel>Classification</SLabel>

              {/* Group */}
              <Field label="Category Group" required>
                <LSelect
                  value={item.groupIndex ?? ""}
                  onChange={v => {
                    up("groupIndex", v);
                    up("categoryId", null);
                    up("subcategoryId", null);
                    up("productId", null);
                  }}
                  options={CATEGORY_GROUPS.map((g, i) => ({ id: i, name: g.name }))}
                  placeholder="Select group…"
                />
              </Field>

              {/* Category */}
              <Field label="Category" required>
                <LSelect
                  value={item.categoryId ?? ""}
                  onChange={v => {
                    up("categoryId", v);
                    up("subcategoryId", null);
                    up("productId", null);
                  }}
                  options={group?.categories ?? []}
                  placeholder="Select category…"
                  disabled={!group}
                />
              </Field>

              {/* Subcategory */}
              <Field label="Subcategory">
                <LSelect
                  value={item.subcategoryId ?? ""}
                  onChange={v => { up("subcategoryId", v); up("productId", null); }}
                  options={subs}
                  placeholder="Select subcategory…"
                  disabled={!item.categoryId || subs.length === 0}
                />
              </Field>

              {/* Product */}
              {prods.length > 0 && (
                <Field label="Product Type">
                  <LSelect
                    value={item.productId ?? ""}
                    onChange={v => up("productId", v)}
                    options={prods}
                    placeholder="Select product…"
                  />
                </Field>
              )}
            </div>

            {/* ══ SECTION 2: PRODUCT DETAILS ══ */}
            <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${T.border}` }}>
              <SLabel>Product Details</SLabel>

              <Field label="Title" required>
                <LInput value={item.title} onChange={v => up("title", v)} placeholder="e.g. Samsung Galaxy S25 Ultra" />
              </Field>

              <Field label="Description">
                <LInput value={item.description} onChange={v => up("description", v)} placeholder="Describe this product…" rows={4} />
              </Field>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="Price" required>
                  <LInput value={item.rate || ""} onChange={v => up("rate", parseFloat(v) || 0)} prefix="€" type="number" />
                </Field>
                <Field label="Stock Quantity">
                  <LInput value={item.stock || ""} onChange={v => up("stock", +v)} type="number" placeholder="0" disabled={hasVariants} />
                </Field>
              </div>
            </div>

            {/* ══ SECTION 3: ATTRIBUTES ══ */}
            {isElectronics && (
              <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${T.border}` }}>
                <SLabel sub="Filter-ready attributes improve discoverability">Attributes & Filters</SLabel>

                {/* Colour swatches */}
                <Field label="Colours">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {electronicsColorAttributes.values.map(c => {
                      const isSel = selectedColors.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          onClick={() => handleColorToggle(c.id, !isSel)}
                          title={c.name}
                          style={{
                            width: 28, height: 28, borderRadius: "50%",
                            background: c.gradient ?? c.hexCodes?.[0] ?? "#ccc",
                            border: isSel ? `2.5px solid ${T.black}` : `1.5px solid ${T.border}`,
                            cursor: "pointer", transition: "all .15s",
                            boxShadow: isSel ? `0 0 0 2px ${T.bg}, 0 0 0 4px ${T.black}` : "none",
                            transform: isSel ? "scale(1.2)" : "scale(1)",
                          }}
                        />
                      );
                    })}
                  </div>
                  {selectedColors.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 11, color: T.muted, fontFamily: "'Montserrat', sans-serif" }}>
                      {selectedColors.length} colour{selectedColors.length > 1 ? "s" : ""} — variant cards below ↓
                    </div>
                  )}
                </Field>

                {/* Other attribute groups */}
                {electronicsAttributes
                  .filter(g => g.id !== electronicsColorAttributes.id)
                  .map((group: ElectronicsAttributeGroup) => {
                    const current = item.attributes?.[group.id];
                    return (
                      <Field key={group.id} label={group.name}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                          {group.values.map(v => {
                            const isSel = Array.isArray(current) ? current.includes(v.id) : current === v.id;
                            return (
                              <button
                                key={v.id}
                                onClick={() => {
                                  const cur = item.attributes?.[group.id];
                                  up("attributes", {
                                    ...item.attributes,
                                    [group.id]: isSel ? undefined : v.id,
                                  });
                                }}
                                style={{
                                  padding: "5px 12px",
                                  border: `1px solid ${isSel ? T.black : T.border}`,
                                  background: isSel ? T.black : T.bg,
                                  color: isSel ? T.bg : T.blackSoft,
                                  fontSize: 11, fontWeight: isSel ? 700 : 400,
                                  fontFamily: "'Montserrat', sans-serif",
                                  letterSpacing: "0.04em", cursor: "pointer",
                                  transition: "all .15s",
                                }}
                              >
                                {v.name}
                              </button>
                            );
                          })}
                        </div>
                      </Field>
                    );
                  })}
              </div>
            )}

            {/* ══ SECTION 4: VARIANTS ══ */}
            {hasVariants && (
              <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${T.border}` }}>
                <SLabel sub="One card per selected colour — upload image & set stock">Variants</SLabel>
                {selectedColors.map(colorId => (
                  <ElectronicsVariantCard
                    key={colorId}
                    colorId={colorId}
                    variant={item.variants?.[colorId]}
                    itemId={item.id}
                    stock={item.stock}
                    onUpdate={updateVariant}
                    onRemove={removeVariantAndColor}
                  />
                ))}
              </div>
            )}

            {/* ══ SECTION 5: MEDIA ══ */}
            <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${T.border}` }}>
              <SLabel sub={`Up to ${MAX_IMAGES} product images`}>Media</SLabel>

              {/* Photo grid */}
              <div style={{
                display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12,
                opacity: hasVariants ? 0.35 : 1,
                pointerEvents: hasVariants ? "none" : "auto",
              }}>
                {(item.photos ?? []).map(url => (
                  <div key={url} style={{ width: 80, height: 96, position: "relative", overflow: "hidden", border: `1px solid ${T.border}` }}>
                    <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button
                      onClick={() => item.listingId && onDelPhoto(item.listingId, url)}
                      style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.65)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <X size={10} color={T.bg} />
                    </button>
                  </div>
                ))}

                {item.images.map((file, i) => (
                  <div key={i} style={{ width: 80, height: 96, position: "relative", overflow: "hidden", border: `1px solid ${T.border}` }}>
                    <img src={URL.createObjectURL(file)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button
                      onClick={() => up("images", item.images.filter((_, idx) => idx !== i))}
                      style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,.65)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <X size={10} color={T.bg} />
                    </button>
                  </div>
                ))}

                {((item.photos?.length ?? 0) + item.images.length) < MAX_IMAGES && !hasVariants && (
                  <label style={{ width: 80, height: 96, border: `1px dashed ${T.borderDark}`, background: T.bgSoft, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 6 }}>
                    <Camera size={18} color={T.muted} />
                    <span style={{ fontSize: 9, color: T.muted, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.1em" }}>ADD</span>
                    <input type="file" hidden accept="image/*" multiple onChange={e => {
                      if (!e.target.files) return;
                      const existing = (item.photos?.length ?? 0) + item.images.length;
                      const slots = MAX_IMAGES - existing;
                      const files = Array.from(e.target.files).slice(0, slots);
                      up("images", [...item.images, ...files]);
                    }} />
                  </label>
                )}
              </div>

              {hasVariants && (
                <div style={{ fontSize: 11, color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 12 }}>
                  Upload images per colour variant above ↑
                </div>
              )}

              {/* Video */}
              <div style={{ border: `1px dashed ${T.borderDark}`, padding: 14, background: T.bgSoft }}>
                <div style={{ fontSize: 9, letterSpacing: "0.18em", color: T.muted, fontFamily: "'Montserrat', sans-serif", marginBottom: 10 }}>VIDEO (OPTIONAL)</div>
                {item.videoFile ? (
                  <div>
                    <video src={URL.createObjectURL(item.videoFile)} controls style={{ width: "100%", maxHeight: 220 }} />
                    <button onClick={() => up("videoFile", null)} style={{ marginTop: 8, background: "none", border: `1px solid ${T.border}`, padding: "6px 14px", cursor: "pointer", fontSize: 10, fontFamily: "'Montserrat', sans-serif", color: T.muted, letterSpacing: "0.1em" }}>
                      REMOVE VIDEO
                    </button>
                  </div>
                ) : item.video ? (
                  <div>
                    <video src={item.video} controls style={{ width: "100%", maxHeight: 220 }} />
                    <button onClick={() => up("video", null)} style={{ marginTop: 8, background: "none", border: `1px solid ${T.border}`, padding: "6px 14px", cursor: "pointer", fontSize: 10, fontFamily: "'Montserrat', sans-serif", color: T.muted, letterSpacing: "0.1em" }}>
                      REPLACE VIDEO
                    </button>
                  </div>
                ) : (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <Video size={16} color={T.muted} />
                    <span style={{ fontSize: 11, color: T.muted, fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.06em" }}>Upload product video…</span>
                    <input type="file" hidden accept="video/mp4,video/quicktime" onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) up("videoFile", f);
                    }} />
                  </label>
                )}
              </div>
            </div>

            {/* ══ SECTION 6: VISIBILITY ══ */}
            <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${T.border}` }}>
              <SLabel>Visibility & Status</SLabel>

              {[
                { label: "Published & Live",     sub: "Visible to all shoppers",       key: "isPublished", val: item.isPublished },
                { label: "Available to Purchase", sub: "Customers can add to cart",     key: "available",   val: item.available  },
              ].map(row => (
                <div key={row.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${T.border}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: T.black, fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.02em" }}>{row.label}</div>
                    <div style={{ fontSize: 10, color: T.muted, fontFamily: "'Montserrat', sans-serif", marginTop: 2 }}>{row.sub}</div>
                  </div>
                  <LuxToggle on={row.val} onChange={v => up(row.key as keyof NewCommerce, v)} />
                </div>
              ))}
            </div>

            {/* ══ ACTIONS ══ */}
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                disabled={!item.edited || saving}
                onClick={() => onSave(item)}
                style={{
                  flex: 1, padding: "14px 0",
                  background: item.edited && !saving ? T.black : T.bgMuted,
                  color: item.edited && !saving ? T.bg : T.muted,
                  border: "none", cursor: item.edited && !saving ? "pointer" : "not-allowed",
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.18em",
                  fontFamily: "'Montserrat', sans-serif", display: "flex",
                  alignItems: "center", justifyContent: "center", gap: 8, transition: "all .2s",
                }}
              >
                {saving ? (
                  <span style={{ width: 14, height: 14, border: `2px solid ${T.bg}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "glamSpin 1s linear infinite" }} />
                ) : (
                  <><Upload size={13} /> SAVE LISTING</>
                )}
              </button>

              {item.listingId && (
                <button
                  onClick={() => { if (window.confirm("Permanently delete this listing?")) onDelete(item.listingId!); }}
                  style={{ width: 48, height: 48, background: T.bg, border: `1px solid ${T.border}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = "#8B0000")}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = T.border)}
                >
                  <Trash2 size={16} color="#8B0000" />
                </button>
              )}
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ════════════════════════════════════════════════════════════════
   MAIN SCREEN
════════════════════════════════════════════════════════════════ */
const AddMetaCommerce: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();

  const userId        = useSelector((s: RootState) => s.auth.user?.userId);
  const partners      = useSelector((s: RootState) => s.commercePartners.partners);
  const partnerLoading = useSelector((s: RootState) => s.commercePartners.loading);
  const listings      = useSelector((s: RootState) => s.classifieds.listings);

  const [items,      setItems]      = useState<NewCommerce[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [savingId,   setSavingId]   = useState<string | null>(null);
  const [snack,      setSnack]      = useState<{ open: boolean; msg: string; type: "ok" | "err" | "warn" }>({ open: false, msg: "", type: "ok" });

  const toast = (msg: string, type: "ok" | "err" | "warn" = "ok") => {
    setSnack({ open: true, msg, type });
    setTimeout(() => setSnack(s => ({ ...s, open: false })), 3000);
  };

  /* ── Listeners ── */
  useEffect(() => { if (userId) dispatch(listenToUserCommercePartners(userId) as any); }, [userId, dispatch]);

  useEffect(() => {
    if (!userId || partnerLoading) return;
    if (partners.length === 0) navigate(`/CommerceOnboarding/${userId}`, { replace: true });
  }, [partners, partnerLoading, userId, navigate]);

  useEffect(() => {
    if (!userId) return;
    dispatch(setListings([]));
    dispatch(listenToHostClassified({ hostId: userId }));
  }, [dispatch, userId]);

  /* ── Populate from Redux ── */
  useEffect(() => {
    setItems(
      (listings as any[]).map((l: ClassifiedListing): NewCommerce => {
        const groupIndex = findGroupIndexByCategoryId(l.categoryId ?? null);
        return {
          id:           l.listingId,
          listingId:    l.listingId,
          groupIndex,
          categoryId:   l.categoryId ?? null,
          subcategoryId: l.subcategoryId ?? null,
          productId:    l.productId ?? null,
          title:        l.name ?? "",
          description:  l.description ?? "",
          stock:        l.stock ?? 0,
          rate:         l.rate ?? 0,
          available:    l.available ?? true,
          attributes:   (l as any).attributes ?? {},
          dimensions:   (l as any).dimensions ?? { unit: "cm", weightUnit: "g" },
          images:       [],
          photos:       l.photos ?? [],
          video:        l.video ?? null,
          videoFile:    null,
          variants:     l.variants
            ? Object.fromEntries(
                Object.entries(l.variants).map(([k, v]: [string, any]) => [
                  Number(k),
                  { imageUrl: v.imageUrl, imageFile: undefined, previewUrl: undefined, sizeIds: v.sizeIds ?? [], stock: v.stock ?? 0 },
                ])
              )
            : {},
          edited:      false,
          isPublished: l.isPublished ?? false,
        };
      })
    );
  }, [listings]);

  /* ── Helpers ── */
  const onUpdate = (id: string, key: keyof NewCommerce, value: any) =>
    setItems(prev => prev.map(i => i.id === id ? { ...i, [key]: value, edited: true } : i));

  const addNew = () => {
    const id = `new_${Date.now()}`;
    setItems(prev => [{
      id, groupIndex: null, categoryId: null, subcategoryId: null, productId: null,
      title: "", description: "", stock: 0, rate: 0, available: true,
      attributes: {}, variants: {}, images: [], photos: [],
      videoFile: null, video: null, edited: true, isPublished: false,
    }, ...prev]);
    setExpandedId(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ── Save ── */
  const onSave = async (item: NewCommerce) => {
    if (!item.title) { toast("Title is required", "warn"); return; }
    setSavingId(item.id);

    try {
      const colorValue = item.attributes?.[electronicsColorAttributes.id];
      const isVariantBased = Array.isArray(colorValue) && colorValue.length > 0;

      const created = await dispatch(upsertClassified({
        listingId: item.listingId,
        data: {
          name:        item.title,
          description: item.description,
          categoryId:  item.categoryId,
          subcategoryId: item.subcategoryId,
          productId:   item.productId,
          rate:        item.rate,
          available:   item.available,
          isPublished: item.isPublished,
          isDraft:     !item.isPublished,
          attributes:  item.attributes ?? {},
          dimensions:  item.dimensions ?? {},
          ...(isVariantBased
            ? {
                variants: Object.fromEntries(
                  Object.entries(item.variants || {})
                    .filter(([, v]) => !!v.imageUrl)
                    .map(([k, v]) => [k, { colorId: +k, imageUrl: v.imageUrl!, sizeIds: v.sizeIds ?? [], stock: v.stock ?? 0 }])
                ),
              }
            : { stock: item.stock }),
        },
      })).unwrap();

      /* Upload variant images */
      if (isVariantBased && item.variants) {
        for (const [colorId, variant] of Object.entries(item.variants)) {
          if (!variant.imageFile) continue;
          const res = await dispatch(upsertClassifiedMedia({ listingId: created.listingId, files: [variant.imageFile], type: "photo" })).unwrap();
          const url = res.downloadURLs?.[0];
          if (url) variant.imageUrl = url;
        }
        await dispatch(upsertClassified({
          listingId: created.listingId,
          data: {
            variants: Object.fromEntries(
              Object.entries(item.variants)
                .filter(([, v]) => !!v.imageUrl)
                .map(([k, v]) => [k, { colorId: +k, imageUrl: v.imageUrl!, sizeIds: v.sizeIds ?? [], stock: v.stock ?? 0 }])
            ),
          },
        })).unwrap();
      }

      if (item.images.length > 0) {
        await dispatch(upsertClassifiedMedia({ listingId: created.listingId, files: item.images, type: "photo" })).unwrap();
      }
      if (item.videoFile) {
        await dispatch(upsertClassifiedMedia({ listingId: created.listingId, files: [item.videoFile], type: "video" })).unwrap();
      }

      toast("Saved successfully");
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, listingId: created.listingId, edited: false, images: [], videoFile: null } : i));
    } catch (err) {
      console.error(err);
      toast("Save failed", "err");
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = (listingId: string) => {
    dispatch(deleteClassified(listingId));
    setItems(prev => prev.filter(i => i.listingId !== listingId));
    toast("Listing removed");
  };

  const onDelPhoto = (listingId: string, url: string) =>
    dispatch(deleteClassifiedPhoto({ listingId, photoUrl: url }));

  const publishedCount = items.filter(i => i.isPublished).length;
  const draftCount     = items.filter(i => !i.isPublished).length;
  const unsavedCount   = items.filter(i => i.edited).length;

  /* ════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: T.bgSoft, fontFamily: "'Montserrat', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300&family=Montserrat:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        @keyframes glamSpin { to { transform: rotate(360deg); } }
        textarea { resize: vertical; }
        select option { color: #0A0A0A; }
        input[type=number]::-webkit-inner-spin-button { opacity: 0.3; }
        input::placeholder, textarea::placeholder { color: #B8B8B4; }
      `}</style>

      {/* ── Page Header ── */}
      <div style={{ background: T.black, padding: "32px 28px 24px", borderBottom: "1px solid #1A1A1A" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)", letterSpacing: "0.2em", fontWeight: 500, marginBottom: 12 }}>
            COMMERCE SELLER STUDIO
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300, color: T.bg, letterSpacing: "0.06em", lineHeight: 1, margin: "0 0 8px" }}>
                Your Inventory
              </h1>
              <div style={{ fontSize: 11, color: T.gold, letterSpacing: "0.18em", fontWeight: 600 }}>
                ELECTRONICS & MORE
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: "flex", gap: 0 }}>
              {[
                { label: "LIVE",    val: publishedCount, col: T.gold },
                { label: "DRAFTS",  val: draftCount,     col: "rgba(255,255,255,.35)" },
                { label: "UNSAVED", val: unsavedCount,   col: unsavedCount > 0 ? "#FDB347" : "rgba(255,255,255,.35)" },
              ].map((s, i) => (
                <div key={s.label} style={{ padding: "8px 18px", borderLeft: i > 0 ? "1px solid rgba(255,255,255,.1)" : "none" }}>
                  <div style={{ fontSize: 22, fontWeight: 300, color: s.col, fontFamily: "'Cormorant Garamond', serif", lineHeight: 1 }}>{s.val}</div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,.3)", letterSpacing: "0.2em", marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 } as any} whileTap={{ scale: .97 } as any}
            onClick={addNew}
            style={{
              marginTop: 20, display: "flex", alignItems: "center", gap: 8,
              background: T.bg, color: T.black, border: "none",
              padding: "12px 24px", cursor: "pointer",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.18em",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            <PlusCircle size={15} /> ADD NEW LISTING
          </motion.button>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "28px 20px 80px" }}>

        {unsavedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: "flex", alignItems: "center", gap: 10, background: "#FDF7E3", border: "1px solid #E8D5A0", padding: "11px 16px", marginBottom: 20 }}
          >
            <AlertCircle size={14} color="#7A5C00" />
            <span style={{ fontSize: 11, color: "#7A5C00", fontFamily: "'Montserrat', sans-serif", fontWeight: 600, letterSpacing: "0.06em" }}>
              {unsavedCount} listing{unsavedCount > 1 ? "s" : ""} with unsaved changes
            </span>
          </motion.div>
        )}

        {items.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{ textAlign: "center", padding: "80px 32px" }}
          >
            <div style={{ fontSize: 64, marginBottom: 20, opacity: 0.15, fontFamily: "'Cormorant Garamond', serif" }}>✦</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, color: T.blackSoft, letterSpacing: "0.04em", marginBottom: 10 }}>
              Your inventory is empty
            </h2>
            <p style={{ fontSize: 12, color: T.muted, fontFamily: "'Montserrat', sans-serif", lineHeight: 1.8, maxWidth: 360, margin: "0 auto 28px" }}>
              Add your first listing and it will appear here. Every product you list brings your store to life.
            </p>
            <button onClick={addNew} style={{ background: T.black, color: T.bg, border: "none", padding: "13px 32px", cursor: "pointer", fontSize: 11, fontWeight: 700, letterSpacing: "0.2em", fontFamily: "'Montserrat', sans-serif" }}>
              ADD FIRST LISTING
            </button>
          </motion.div>
        )}

        <div>
          {items.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
              onUpdate={onUpdate}
              onSave={onSave}
              onDelete={onDelete}
              onDelPhoto={onDelPhoto}
              saving={savingId === item.id}
            />
          ))}
        </div>

        {items.length > 0 && (
          <div style={{ textAlign: "center", paddingTop: 32 }}>
            <button onClick={addNew} style={{ background: "none", border: `1px solid ${T.border}`, padding: "12px 28px", cursor: "pointer", fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", color: T.muted, fontFamily: "'Montserrat', sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}>
              <PlusCircle size={13} /> ADD ANOTHER LISTING
            </button>
          </div>
        )}
      </div>

      <Snack visible={snack.open} msg={snack.msg} type={snack.type} />
    </div>
  );
};

export default AddMetaCommerce;